import React, { useMemo, useRef, useState } from "react"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { integrationsUploadEraFile } from "../../../store/thunks/integrations.thunks"
import type { IEraUploadReviewState } from "../../../types/integrations.types"
import { EraUploadBulkUploadPopupComponent } from "../../popups/eraUploadBulkUploadPopup.component"
import { EraUploadReviewFloatingControlComponent } from "./eraUploadReviewFloatingControl.component"
import { EraUploadTableComponent } from "./eraUploadTable.component"
import { EraUploadReviewX12PanelComponent } from "./eraUploadReviewX12Panel.component"

import closeDarkImg from "@front-shared/assets/images/icons/close-dark.svg"

export const EraUploadReviewComponent: React.FunctionComponent<
    IEraUploadReviewComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const scrollableTableRef = useRef<HTMLDivElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const runtimeState = useAppSelector(runtimeSelector)

    const isUploading = useMemo<boolean>(() => {
        return RuntimeHelper.isIntegrationsEraUploadLoading()
    }, [runtimeState.isLoading])

    const [isReuploadPopupActive, setIsReuploadPopupActive] =
        useState<boolean>(false)

    const isX12Upload =
        props.state.fileFormat === OneVerifyEraUploadFileFormat.x12

    const isPreviewTruncated =
        props.state.totalRowCount > props.state.rows.length

    const onReupload = () => {
        setIsReuploadPopupActive(true)
    }

    const onUpload = () => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            integrationsUploadEraFile({
                practice: selectedPracticeRole.practice,
                payload: {
                    file: props.state.file,
                    fileFormat: props.state.fileFormat,
                    rowCount: props.state.totalRowCount,
                },
                onSuccess: () => {
                    props.onUploadSuccess()
                },
            }),
        )
    }

    return (
        <div className={`pb-32 relative w-full ${props.className ?? ""}`}>
            <NirvanaBreathingLoaderElement isActive={isUploading} />

            <div className="max-w-[960px] w-full">
                <div className="relative py-[12px] mb-[24px]">
                    <div className="flex items-center flex-wrap gap-[16px]">
                        <PrimaryText
                            size={29}
                            lineHeight={35}
                            weight="semibold"
                            className="mr-[8px]"
                        >
                            Review ERA Upload
                        </PrimaryText>

                        <span
                            className="
                                flex items-center px-[8px] py-[2px]
                                rounded-[4px] bg-brand-white
                            "
                        >
                            <PrimaryText typography="h6" className="mr-[4px]">
                                {props.state.filename}
                            </PrimaryText>

                            <button
                                type="button"
                                onClick={props.onClose}
                                className="p-[4px] cursor-pointer"
                                aria-label="Close review"
                            >
                                <img src={closeDarkImg} alt="Close" />
                            </button>
                        </span>

                        {isX12Upload ? (
                            <PrimaryText
                                typography="subtitle"
                                className="w-full sm:w-auto"
                            >
                                X12 835 file ready for upload.
                            </PrimaryText>
                        ) : isPreviewTruncated ? (
                            <PrimaryText
                                typography="subtitle"
                                className="w-full sm:w-auto"
                            >
                                Previewing first {props.state.rows.length} rows
                                — {props.state.totalRowCount.toLocaleString()}{" "}
                                total will upload.
                            </PrimaryText>
                        ) : null}
                    </div>
                </div>
            </div>

            {isX12Upload ? (
                <div className="max-w-[960px] w-full pb-[120px]">
                    <EraUploadReviewX12PanelComponent state={props.state} />
                </div>
            ) : (
                <div
                    className="
                        relative w-[calc(100%+90px)] -mx-[45px]
                        sm:w-[calc(100%+32px)] sm:-mx-[16px]
                        pb-[120px]
                    "
                >
                    <EraUploadTableComponent
                        scrollableElementRef={scrollableTableRef}
                        rows={props.state.rows}
                    />
                </div>
            )}

            <EraUploadReviewFloatingControlComponent
                fileFormat={props.state.fileFormat}
                previewRowCount={props.state.rows.length}
                totalRowCount={props.state.totalRowCount}
                isPreviewTruncated={isPreviewTruncated}
                onReupload={onReupload}
                onUpload={onUpload}
                isUploading={isUploading}
            />

            <EraUploadBulkUploadPopupComponent
                isActive={isReuploadPopupActive}
                fileFormat={props.state.fileFormat}
                onDone={() => {
                    setIsReuploadPopupActive(false)
                }}
                onValidationDone={data => {
                    setIsReuploadPopupActive(false)
                    props.onReuploadWithNewFile(data)
                }}
            />
        </div>
    )
}

interface IEraUploadReviewComponentProps {
    className?: string
    state: IEraUploadReviewState
    onClose: () => void
    onReuploadWithNewFile: (data: IEraUploadReviewState) => void
    onUploadSuccess: () => void
}
